import { PosStore } from './store';

/**
 * Basar-Sitzungen der Kassen (SYN-10) gehören zum Gerät. Damit die Sitzung einer Kassiererin nach der Abmeldung nicht für
 * die nächste Person am selben Gerät weitergilt, wird sie beim Abmelden aus dem lokalen Speicher entfernt. Die Kasse und
 * die Warteschlange bleiben unberührt: Nach der Anmeldung wird eine neue Sitzung geholt.
 */
const INDEX_KEY = 'barso.pos.events';

function known(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(INDEX_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is string => typeof entry === 'string')
      : [];
  } catch {
    return [];
  }
}

const RECENT_KEY = 'barso.pos.recent';
const RECENT_MAX = 10;

/** Zuletzt benutzte Kasse dieses Geräts: nur Kennung und Basar-Titel (keine Personendaten), für den Einstieg mit einem Tipp. */
export interface RecentPosEvent {
  eventId: string;
  title: string | null;
  /** Kasse mit Schnellzugang (E11): Der Einstieg führt auf die Geräte-Seite ohne Anmeldung. */
  device?: true;
}

/** Zuletzt benutzte Kassen, die zuletzt benutzte zuerst. Ohne Speicher oder bei kaputten Einträgen: leer. */
export function lastPosEvents(): RecentPosEvent[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry: unknown) => {
      if (typeof entry !== 'object' || entry === null) return [];
      const { eventId, title, device } = entry as {
        eventId?: unknown;
        title?: unknown;
        device?: unknown;
      };
      if (typeof eventId !== 'string' || eventId === '') return [];
      return [
        {
          eventId,
          title: typeof title === 'string' ? title : null,
          ...(device === true ? { device: true as const } : {}),
        },
      ];
    });
  } catch {
    return [];
  }
}

function writeRecent(list: RecentPosEvent[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // ohne Speicher entfällt nur die Abkürzung
  }
}

/** Merkt sich, für welche Basare dieses Gerät eine Kasse hat (nur Kennungen, keine Geheimnisse), und die zuletzt benutzte. */
export function rememberPosEvent(eventId: string, device = false): void {
  try {
    const events = known();
    if (!events.includes(eventId)) {
      localStorage.setItem(INDEX_KEY, JSON.stringify([...events, eventId].slice(-50)));
    }
  } catch {
    // Ohne localStorage bleibt es bei der Sitzung im Kassenspeicher: die Abmeldung räumt dann nicht auf
  }
  const recent = lastPosEvents();
  const title = recent.find((entry) => entry.eventId === eventId)?.title ?? null;
  writeRecent([
    { eventId, title, ...(device ? { device: true as const } : {}) },
    ...recent.filter((entry) => entry.eventId !== eventId),
  ]);
}

/** Entfernt eine Kasse aus der Liste der zuletzt benutzten (z. B. wenn ihr lokaler Speicher fehlt). */
export function forgetRecentPosEvent(eventId: string): void {
  const recent = lastPosEvents();
  if (recent.some((entry) => entry.eventId === eventId)) {
    writeRecent(recent.filter((entry) => entry.eventId !== eventId));
  }
}

/** Trägt den Basar-Titel einer schon gemerkten Kasse nach (die Basar-Hülle kennt ihn, die Kasse offline nicht). */
export function setPosEventTitle(eventId: string, title: string): void {
  const recent = lastPosEvents();
  if (!recent.some((entry) => entry.eventId === eventId && entry.title !== title)) return;
  writeRecent(
    recent.map((entry) => (entry.eventId === eventId ? { ...entry, eventId, title } : entry)),
  );
}

/** Namen der vorhandenen Datenbanken; `undefined`, wenn der Browser die Auflistung nicht kann. */
async function existingDatabases(): Promise<Set<string> | undefined> {
  try {
    if (typeof indexedDB.databases !== 'function') return undefined;
    const list = await indexedDB.databases();
    return new Set(list.flatMap((entry) => (entry.name === undefined ? [] : [entry.name])));
  } catch {
    return undefined;
  }
}

/** Entfernt die Sitzungen aller bekannten Kassen dieses Geräts. Fehler einzelner Speicher stoppen den Rest nicht. */
export async function clearRegisterSessions(): Promise<void> {
  const existing = await existingDatabases();
  for (const eventId of known()) {
    const name = `barso-pos-${eventId}`;
    // Fehlende Speicher nicht neu anlegen (PosStore.open würde sie erzeugen)
    if (existing !== undefined && !existing.has(name)) continue;
    try {
      const store = await PosStore.open(name);
      try {
        // Eine Kasse mit Schnellzugang gehört nicht zu einem Konto: Ihr Geräte-Token bleibt (sonst wäre das Gerät ausgesperrt)
        if ((await store.register())?.mode !== 'name') await store.setSession(undefined);
        // Wartende Kaufbelege tragen E-Mail-Adressen der Kundschaft: Sie bleiben nach der Abmeldung nicht auf dem Gerät
        await store.purgeInvoices();
      } finally {
        store.close();
      }
    } catch {
      // Speicher gesperrt oder nicht vorhanden: nichts zu entfernen
    }
  }
  // Auch die Liste der zuletzt benutzten Kassen (Basar-Titel) bleibt nicht auf dem Gerät zurück
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    // ohne Speicher gibt es auch nichts zu entfernen
  }
}

/**
 * Räumt beim Start der App auf, ohne dass eine Kassenseite offen sein muss: Wartende Kaufbelege (mit Adresse), die älter als
 * `maxAgeMs` sind, werden gelöscht. Fehler einzelner Speicher stoppen den Rest nicht.
 */
export async function purgeStaleInvoices(nowMs: number, maxAgeMs: number): Promise<void> {
  const existing = await existingDatabases();
  for (const eventId of known()) {
    const name = `barso-pos-${eventId}`;
    if (existing !== undefined && !existing.has(name)) continue;
    try {
      const store = await PosStore.open(name);
      try {
        await store.purgeInvoices(nowMs - maxAgeMs);
      } finally {
        store.close();
      }
    } catch {
      // Speicher gesperrt oder nicht vorhanden: nichts zu löschen
    }
  }
}

/** Wartezeit je Datenbank beim Zählen: Die Abmeldung darf nie an einem hängenden Speicher warten. */
const COUNT_TIMEOUT_MS = 2000;

/**
 * Öffnet eine Datenbank nur lesend ohne Versionsnummer: Es wird nichts angelegt und nichts auf eine neue Version
 * gehoben. `null`, wenn es sie nicht gibt.
 */
function openExisting(name: string): Promise<IDBDatabase | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name);
    let missing = false;
    request.onupgradeneeded = () => {
      // Die Datenbank existierte nicht: Anlegen abbrechen
      missing = true;
      request.transaction?.abort();
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = (event) => {
      // Der Abbruch meldet einen Fehler: das ist hier der Normalfall „nicht vorhanden“
      event.preventDefault();
      if (missing) resolve(null);
      else reject(request.error ?? new Error('IndexedDB nicht verfügbar'));
    };
    request.onblocked = () => {
      reject(new Error('IndexedDB blockiert'));
    };
  });
}

/** Wartende Vorgänge eines Speichers: `count` Verkäufe und Stornos, `side` Kommentare und Fotos (U17f/U17h, kein Geld). */
interface StoreCount {
  count: number;
  side: number;
}

async function countRows(db: IDBDatabase): Promise<StoreCount> {
  const names = ['outbox', 'voids', 'notes', 'photos'].filter((name) =>
    db.objectStoreNames.contains(name),
  );
  if (names.length === 0) return { count: 0, side: 0 };
  const transaction = db.transaction(names, 'readonly');
  const result: StoreCount = { count: 0, side: 0 };
  for (const name of names) {
    const rows = await new Promise<unknown[]>((resolve, reject) => {
      const request = transaction.objectStore(name).getAll();
      request.onsuccess = () => {
        resolve(request.result as unknown[]);
      };
      request.onerror = () => {
        reject(request.error ?? new Error('IndexedDB-Fehler'));
      };
    });
    if (name === 'notes') {
      // Noch nicht bestätigte Kommentare (nicht als unzustellbar markiert)
      result.side += rows.filter((row) => {
        const note = row as { status?: unknown; sentAtMs?: unknown } | null;
        return note?.status === 'pending' && note.sentAtMs === undefined;
      }).length;
    } else if (name === 'photos') {
      // Fotos, die zu einem Verkauf gehören und noch hochgeladen werden
      result.side += rows.filter(
        (row) => (row as { saleClientUuid?: unknown } | null)?.saleClientUuid !== undefined,
      ).length;
    } else {
      result.count += rows.filter((row) => {
        const status = (row as { status?: unknown } | null)?.status;
        return status === 'pending' || status === 'failed';
      }).length;
    }
  }
  return result;
}

/** Alles, was noch hinausgehen muss: Verkäufe und Stornos, aber auch wartende Kommentare und Fotos (nichts davon darf verloren gehen). */
async function countUnsent(db: IDBDatabase): Promise<number> {
  const { count, side } = await countRows(db);
  return count + side;
}

async function countStore(name: string): Promise<StoreCount> {
  const state: { db: IDBDatabase | null; timedOut: boolean } = { db: null, timedOut: false };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const work = (async () => {
    const db = await openExisting(name);
    if (db === null) return { count: 0, side: 0 };
    state.db = db;
    if (state.timedOut) {
      db.close();
      return { count: 0, side: 0 };
    }
    try {
      return await countRows(db);
    } finally {
      db.close();
    }
  })();
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      state.timedOut = true;
      state.db?.close();
      reject(new Error('Zeitüberschreitung'));
    }, COUNT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
    // Ein später fehlschlagendes `work` darf keine unbehandelte Ablehnung erzeugen
    work.catch(() => undefined);
  }
}

/** Wartende Kassenvorgänge einer Kasse dieses Geräts. `unknown`: der Speicher ließ sich nicht prüfen. */
export interface UnsentRegister {
  eventId: string;
  count: number;
  /** Wartende Kommentare und Fotos (kein Geld, nicht in `count`): Anlass für den Hintergrund-Sender (U17f/U17h). */
  side?: number;
  unknown: boolean;
}

/**
 * Zählt je Kasse dieses Geräts die noch nicht an den Server übertragenen Vorgänge (wartende oder abgelehnte Verkäufe und
 * Stornos). Reiner Lesezugriff ohne Nebenwirkung. Lässt sich ein Speicher nicht prüfen (Fehler, gesperrt, Zeitüberschreitung),
 * ist `unknown` gesetzt, statt still 0 anzunehmen. Kassen ohne Speicher fehlen in der Liste.
 */
export async function countUnsentByRegister(): Promise<UnsentRegister[]> {
  const existing = await existingDatabases();
  const result: UnsentRegister[] = [];
  for (const eventId of known()) {
    const name = `barso-pos-${eventId}`;
    if (existing !== undefined && !existing.has(name)) continue;
    try {
      result.push({ eventId, ...(await countStore(name)), unknown: false });
    } catch {
      result.push({ eventId, count: 0, side: 0, unknown: true });
    }
  }
  return result;
}

/** Summe über alle Kassen dieses Geräts (siehe `countUnsentByRegister`). */
export async function countUnsentSales(): Promise<{ count: number; unknown: boolean }> {
  const registers = await countUnsentByRegister();
  return {
    count: registers.reduce((sum, entry) => sum + entry.count, 0),
    unknown: registers.some((entry) => entry.unknown),
  };
}

/** Was auf diesem Gerät für einen Basar schon an Kasse vorhanden ist (Kopplungsseite, E11). */
export interface LocalRegisterInfo {
  exists: boolean;
  label: string | null;
  mode: 'login' | 'name' | null;
  /** Noch nicht übertragene oder abgelehnte Verkäufe und Stornos sowie wartende Kommentare und Fotos. */
  unsent: number;
  /** Der Speicher ließ sich nicht prüfen: wie „es gibt wartende Verkäufe“ behandeln. */
  unknown: boolean;
}

const NO_LOCAL_REGISTER: LocalRegisterInfo = {
  exists: false,
  label: null,
  mode: null,
  unsent: 0,
  unknown: false,
};

/**
 * Prüft nur lesend, ob auf diesem Gerät für den Basar schon eine Kasse eröffnet ist (nichts wird angelegt oder gelöscht). Die
 * Kopplungsseite kuppelt nie über eine vorhandene Kasse mit Verkäufen hinweg.
 */
export async function describeLocalRegister(eventId: string): Promise<LocalRegisterInfo> {
  const name = `barso-pos-${eventId}`;
  const existing = await existingDatabases();
  if (existing !== undefined && !existing.has(name)) return NO_LOCAL_REGISTER;
  try {
    const db = await openExisting(name);
    if (db === null) return NO_LOCAL_REGISTER;
    try {
      if (!db.objectStoreNames.contains('meta')) return NO_LOCAL_REGISTER;
      const meta = await new Promise<{ label?: unknown; mode?: unknown } | undefined>(
        (resolve, reject) => {
          const request = db.transaction(['meta'], 'readonly').objectStore('meta').get('register');
          request.onsuccess = () => {
            resolve(request.result as { label?: unknown; mode?: unknown } | undefined);
          };
          request.onerror = () => {
            reject(request.error ?? new Error('IndexedDB-Fehler'));
          };
        },
      );
      if (meta === undefined) return NO_LOCAL_REGISTER;
      return {
        exists: true,
        label: typeof meta.label === 'string' ? meta.label : null,
        mode: meta.mode === 'name' || meta.mode === 'login' ? meta.mode : null,
        unsent: await countUnsent(db),
        unknown: false,
      };
    } finally {
      db.close();
    }
  } catch {
    return { exists: true, label: null, mode: null, unsent: 0, unknown: true };
  }
}

/**
 * Entfernt die lokale Kasse eines Basars, aber nur, wenn nichts mehr wartet (alles übertragen) und sich der Speicher sicher
 * prüfen ließ. Sonst wird nichts gelöscht.
 */
export async function forgetLocalRegister(eventId: string): Promise<void> {
  const info = await describeLocalRegister(eventId);
  if (!info.exists) return;
  if (info.unsent > 0 || info.unknown) {
    throw new Error('Es warten noch Verkäufe auf dieser Kasse. Erst senden, dann entfernen.');
  }
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(`barso-pos-${eventId}`);
    request.onsuccess = () => {
      resolve();
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB-Fehler'));
    };
    request.onblocked = () => {
      reject(new Error('Die Kasse ist in einem anderen Tab geöffnet. Bitte dort schließen.'));
    };
  });
}
